import { alpha, Box, Popper, Stack, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import _ from 'lodash';
import APIDocuments from '../apiDocuments';
import { useDispatch, useSelector } from 'react-redux';
import { getDocPageId } from '../store/methods';
import { addCurrentToolbarThumbnailPageData } from '../store/documentViewerReducer';
import { Comments } from '../assets/icons';
import { getConfig } from '../../../util/configUtil';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        '--primaryColor': theme.palette.primary.main,
        '--comment-bg': alpha(theme.palette.primary.main, 0.2),
        '--shadow-color': theme.palette.documentViewer.paperBackgroundColor,
        '--shadow-container': 'rgba(255, 255, 255, 0.35)',
        '--grey': theme.palette.documentViewer.thumbnailGray,
    },
    pageContainer: {
        maxWidth: '158px',
        maxHeight: '200px',
        minWidth: '70px',
        minHeight: '70px',
        background: 'var(--shadow-container)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        borderRadius: '12px',
        boxShadow: '0 3.94px 7.88px 1.97px var(--shadow-color)',
    },
    image: {
        maxWidth: '156px',
        maxHeight: '198px',
        borderRadius: '12px',
    },
    pageNumberContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '10px',
    },
    pageNumber: {
        minWidth: '24px',
        padding: '0px 8px',
        borderRadius: '50%',
        background: theme.palette.documentViewer.buttonsBackgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '500',
        '&.active': {
            fontWeight: 'bold',
            boxShadow: '0 3.94px 7.88px 1.97px var(--comment-bg)',
            color: 'var(--primaryColor)',
        },
    },
    bookmarkRibbon: {
        width: '0',
        height: '24px',
        borderRight: '9px solid var(--grey)',
        borderLeft: '9px solid var(--grey)',
        borderBottom: '5px solid transparent',
        position: 'absolute',
        right: '15px',
        top: '0px',
        cursor: 'help',
    },
    annotationMark: {
        borderRadius: '50%',
        border: '0.5px solid var(--primaryColor)',
        background: 'var(--comment-bg)',
        padding: '6px',
        position: 'absolute',
        color: 'var(--primaryColor)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    popover: {
        zIndex: 100,
        '&[data-popper-placement*="bottom"] .arrow': {
            top: 0,
            left: 0,
            marginTop: 0,
            width: '3em',
            height: '1em',
            '&::before': {
                borderWidth: '0 1.5em 1.5em 1.5em',
                borderColor: `transparent transparent ${theme.palette.documentViewer.paperBackgroundColor} transparent`,
            },
        },
        '&[data-popper-placement*="top"] .arrow': {
            bottom: 0,
            left: '-0.2rem',
            marginBottom: '-1.2em',
            width: '3em',
            height: '1em',
            '&::before': {
                borderWidth: '1.5em 1.5em 0 1.5em',
                borderColor: `${theme.palette.documentViewer.paperBackgroundColor} transparent transparent transparent`,
            },
        },
        '&[data-popper-placement*="right"] .arrow': {
            left: 0,
            marginLeft: '-1.2em',
            height: '3em',
            width: '1em',
            '&::before': {
                borderWidth: '1.5em 1.5em 1.5em 0',
                borderColor: `transparent ${theme.palette.documentViewer.paperBackgroundColor} transparent transparent`,
            },
        },
        '&[data-popper-placement*="left"] .arrow': {
            right: 0,
            marginRight: '-1.2em',
            height: '3em',
            width: '1em',
            '&::before': {
                borderWidth: '1.5em 0 1.5em 1.5em',
                borderColor: `transparent transparent transparent ${theme.palette.documentViewer.paperBackgroundColor}`,
            },
        },
    },
    bookmarkPopover: {
        marginTop: '10px',
        marginRight: '-12px',
        background: theme.palette.documentViewer.paperBackgroundColor,
        borderRadius: '3px',
        padding: '4px 8px',
    },
    bookmarkAnnotation: {
        width: '200px',
        maxHeight: '300px',
        wordWrap: 'break-word',
        overflowY: 'auto',
    },
    bookmarkRibbonUser: {
        fontWeight: '700',
        fontSize: '12px',
    },
    bookmarkRibbonDate: {
        fontStyle: 'italic',
        fontWeight: '300',
        fontSize: '12px',
    },
    arrow: {
        position: 'absolute',
        fontSize: 8,
        width: '3em',
        height: '3em',
        marginRight: '3px',
        '&::before': {
            content: '""',
            margin: 'auto',
            display: 'block',
            width: 0,
            height: 0,
            borderStyle: 'solid',
            marginLeft: '3px',
        },
    },
}));

export function ThumbnailPage(props) {
    const innerRef = React.useRef();
    const [arrowRef, setArrowRef] = React.useState(null);
    const classes = useStyles();
    const dispatch = useDispatch();
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const currentToolbarThumbnailsData = useSelector((state) =>
        state.documentViewer.currentToolbarSet !== null ? state.documentViewer.currentToolbarSet.thumbnailsData : {},
    );
    const currentToolbarAllBookmarks = useSelector((state) =>
        state.documentViewer.currentToolbarSet !== null ? state.documentViewer.currentToolbarSet.allBookmarks : [],
    );
    const currentToolbarAllAnnotations = useSelector((state) =>
        state.documentViewer.currentToolbarSet !== null ? state.documentViewer.currentToolbarSet.allAnnotations : [],
    );
    const activePage = useSelector((state) =>
        state.documentViewer.currentToolbarSet !== null ? state.documentViewer.currentToolbarSet.activePage : 0,
    );
    const [thumbPageImg, setThumbPageImg] = React.useState(null);
    const [hasBookmark, setHasBookmark] = React.useState(true);
    const [bookmarkAnnotation, setBookmarkAnnotation] = React.useState('');
    const [bookmarkUser, setBookmarkUser] = React.useState('');
    const [bookmarkDate, setBookmarkDate] = React.useState('');
    const [hasAnnotations, setHasAnnotations] = React.useState(false);
    const [bookmarkAnchorEl, setBookmarkAnchorEl] = React.useState(null);
    const debouncedHandleMouseEnter = _.debounce((target) => {
        setBookmarkAnchorEl(target);
    }, 500);
    const handlePopoverClose = () => {
        setOpenBookmarkPopover(false);
        setBookmarkAnchorEl(null);
        debouncedHandleMouseEnter.cancel();
    };
    const [openBookmarkPopover, setOpenBookmarkPopover] = React.useState(false);
    const [parentScrollPosition, setParentScrollPosition] = React.useState(-1);
    const [iBecomeVisible, setIBecomeVisible] = React.useState(false);

    React.useEffect(() => {
        setOpenBookmarkPopover(Boolean(bookmarkAnchorEl));
    }, [bookmarkAnchorEl]);

    React.useEffect(() => {
        if (props.documentId && props.page) {
            const docPage = getDocPageId(props.documentId, props.page);
            if (currentToolbarThumbnailsData.hasOwnProperty(docPage)) setThumbPageImg(currentToolbarThumbnailsData[docPage]);
            else {
                if (iBecomeVisible)
                    APIDocuments.getDocPageThumbnail(props.documentId, props.page).then((image) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            setThumbPageImg(reader.result);
                            dispatch(addCurrentToolbarThumbnailPageData({ docPageId: docPage, thumbData: reader.result }));
                        };
                        reader.readAsDataURL(image);
                    });
            }
            const bmk = _.find(currentToolbarAllBookmarks, (bookmark) => bookmark.pageNumber === props.page);
            if (bmk) {
                setHasBookmark(true);
                setBookmarkAnnotation(bmk.note);
                setBookmarkUser(`${bmk.userFirstName} ${bmk.userLastName}`);
                setBookmarkDate(moment(bmk.createdDate).format(defaultDateTimeFormat));
            } else {
                setHasBookmark(false);
                setBookmarkAnnotation('');
                setBookmarkUser('');
                setBookmarkDate('');
            }
            if (_.find(currentToolbarAllAnnotations, (annotation) => annotation.page === props.page)) setHasAnnotations(true);
            else setHasAnnotations(false);
        }
    }, [iBecomeVisible, props.documentId, props.page, currentToolbarAllBookmarks, currentToolbarAllAnnotations]);

    React.useLayoutEffect(() => {
        if (innerRef.current && props.parentRef && props.parentRef.current) {
            const dim = innerRef.current.getBoundingClientRect();
            const parentDim = props.parentRef.current.getBoundingClientRect();
            if (parentDim.top <= dim.y + dim.height && dim.y < parentDim.top + parentDim.height) {
                setIBecomeVisible(true);
            }
        }
    }, [parentScrollPosition]);

    React.useEffect(() => {
        const handleScroll = () => {
            setParentScrollPosition(props.parentRef.current ? props.parentRef.current.scrollTop : 0);
        };
        if (props.parentRef.current) {
            props.parentRef.current.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (props.parentRef.current) {
                props.parentRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [props.parentRef]);

    return (
        <Box className={classes.root} ref={innerRef}>
            <Box className={classes.pageContainer}>
                {thumbPageImg && <Box className={classes.image} component="img" src={thumbPageImg} alt={`Page ${props.page}`} />}
                {thumbPageImg == null && (
                    <Box className={classes.image}>
                        <CircularProgress aria-label="Loading…" size={40} />
                    </Box>
                )}
                {hasBookmark && (
                    <div
                        className={classes.bookmarkRibbon}
                        aria-owns={openBookmarkPopover ? 'mouse-over-popover' : undefined}
                        aria-haspopup="true"
                        onMouseEnter={(ev) => {
                            debouncedHandleMouseEnter(ev.target);
                        }}
                        onMouseLeave={handlePopoverClose}
                    ></div>
                )}
                {hasAnnotations && (
                    <div className={classes.annotationMark}>
                        <Comments />
                    </div>
                )}
                <Popper
                    className={classes.popover}
                    placement="bottom-end"
                    disablePortal={false}
                    modifiers={[
                        {
                            name: 'flip',
                            enabled: false,
                            options: {
                                altBoundary: true,
                                rootBoundary: 'document',
                                padding: 8,
                            },
                        },
                        {
                            name: 'preventOverflow',
                            enabled: false,
                            options: {
                                altAxis: false,
                                altBoundary: false,
                                tether: false,
                                rootBoundary: 'document',
                                padding: 8,
                            },
                        },
                        {
                            name: 'arrow',
                            enabled: true,
                            options: {
                                element: arrowRef,
                            },
                        },
                    ]}
                    open={openBookmarkPopover}
                    anchorEl={bookmarkAnchorEl}
                >
                    <Stack direction={'column'} gap={'8px'} className={classes.bookmarkPopover}>
                        <Box className={classes.bookmarkAnnotation}>{bookmarkAnnotation}</Box>
                        <Stack direction={'column'} gap={'1px'}>
                            <Box className={classes.bookmarkRibbonUser}>{bookmarkUser}</Box>
                            <Box className={classes.bookmarkRibbonDate}>{bookmarkDate}</Box>
                        </Stack>
                    </Stack>
                    <Box component="span" className={`arrow ${classes.arrow}`} ref={setArrowRef} />
                </Popper>
            </Box>
            <Box className={classes.pageNumberContainer}>
                <Box className={`${classes.pageNumber}  ${activePage === props.page ? 'active' : ''}`}>{props.page}</Box>
            </Box>
        </Box>
    );
}
